import type { AnalyticsEventsMap } from "./analytics.d"
import * as ga from "./ga"

export type { AnalyticsEventsMap }

export type AnalyticsEventName = keyof AnalyticsEventsMap

/**
 * An object with methods named as Google Analytics events.
 * The methods‘ arguments correspond to the Google Analytics events payloads.
 *
 * @example
 * ```ts
 * analytics.page_view()
 * ```
 */
export default new Proxy(
  {},
  {
    get:
      (_, eventName: AnalyticsEventName) =>
      (...args: unknown[]) => {
        ga.event(eventName, ...args)

        notify(eventName, args[0])
      },
  },
) as {
  [event in keyof AnalyticsEventsMap]: AnalyticsEventsMap[event] extends void
    ? () => void
    : (payload: AnalyticsEventsMap[event]) => void
}

/**
 * LIQA analytics event detail.
 * @example
 * ```js
 * function handleLiqaAnalyticsEvent(event) {
 *   const { name, payload } = event.detail
 *
 *   if (name === "photo_capture") {
 *     if (payload.mode === "auto")
 *       console.log("LIQA has automatically captured a photo")
 *     if (payload.mode === "manual")
 *       console.log("User has manually captured a photo")
 *   }
 *
 *   if (name === "photo_upload")
 *     console.log("User has uploaded a photo")
 *
 *   if (name === "photo_retake")
 *     console.log("User decided to retake the photo")
 *   if (name === "photo_confirm")
 *     console.log("User has confirmed the photo")
 * }
 * ```
 * @category Analytics
 */
export type AnalyticsDetails = {
  [K in keyof AnalyticsEventsMap]: {
    /** The name of the analytics event. One of the {@link AnalyticsEventsMap} keys. */
    name: K
    /** A payload of the corresponding analytics event. See {@link AnalyticsEventsMap} for details. */
    payload: AnalyticsEventsMap[K]
  }
}[keyof AnalyticsEventsMap]

export type EventListener = (name: string, payload: any) => void

const listeners = new Set<EventListener>()

const notify = (name: AnalyticsEventName, payload: any) => {
  for (const listener of listeners) listener(name, payload)
}

/**
 * Subscribes the given function to the the events passing through the analytics
 * @returns unsubscribe callback
 */
export const onEvent = (listener: EventListener) => {
  listeners.add(listener)

  return () => listeners.delete(listener)
}
